"use client";

import { FaInfoCircle, FaLandmark } from "react-icons/fa";

import Tooltip from "@/components/ui/tooltip";

import CardIcon from "@/lib/billing/get-card-icon";
import { formatDate, formatToHumanReadable } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

import type { MerchantAccount } from "@/types/merchant-account";
import type { PaymentMethod } from "@/types/payment";

type PaymentMethodCard = Omit<
  PaymentMethod,
  "id" | "patientId" | "rainforestPaymentMethodId" | "fingerprint"
>;

interface PaymentMethodCardProps {
  method: PaymentMethodCard;
  merchantAccounts?: MerchantAccount[];
  /** "card" = full card layout (billing tab), "compact" = inline (sidebar) */
  variant?: "card" | "compact";
  /** Right-side slot for actions (e.g. dropdown menu). Rendered without extra tooltip. */
  actions?: React.ReactNode;
  /** Whether card appears selected (for checkout flow) */
  isSelected?: boolean;
  /** Whether card is available for selection (for checkout flow) */
  isAvailable?: boolean;
  /** Whether card is clickable (for checkout flow) */
  isSelectable?: boolean;
  onSelect?: () => void;
  className?: string;
}

/** "Pays Glow VIP" when the method backs a membership, so staff can tell it apart. */
function MembershipLine({ names }: { names?: string[] }) {
  if (!names?.length) return null;
  return (
    <span className="block min-w-0 truncate text-caption-strong text-status-info-text">
      Pays {names.join(", ")}
    </span>
  );
}

function getBankAccountsTooltip(
  method: PaymentMethodCard,
  merchantAccountNameMap: Map<string, string>,
): string {
  // availableMerchantIds: null = all merchants, [] = none, [...] = restricted
  const ids = method.availableMerchantIds ?? null;
  if (ids == null) return "Can be used in all bank accounts";
  if (ids.length === 0) return "Not available for any bank account";
  const uniqueNames = Array.from(
    new Set(ids.map((id) => merchantAccountNameMap.get(id) || id)),
  );
  return `Can be used in: ${uniqueNames.join(", ")}`;
}

export function PaymentMethodCard({
  method,
  merchantAccounts = [],
  variant = "card",
  actions,
  isSelected = false,
  isAvailable = true,
  isSelectable = false,
  onSelect,
  className,
}: PaymentMethodCardProps) {
  const merchantAccountNameMap = new Map(
    merchantAccounts.map((ma) => [ma.id, ma.name]),
  );
  const bankAccountsTooltip = getBankAccountsTooltip(
    method,
    merchantAccountNameMap,
  );

  if (variant === "compact") {
    return (
      <div
        className={cx(
          "relative flex items-start gap-2 rounded-md py-1",
          className,
        )}
      >
        <div className="mt-0.5 shrink-0">
          {method.brand ? (
            <CardIcon paymentMethod={method} />
          ) : (
            <FaLandmark className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground">
            <span>
              {method.brand
                ? `${formatToHumanReadable(method.brand)} •••• ${method.last4}`
                : `${method.bankName || "Bank Account"} •••• ${method.accountNumberLast4 || method.last4}`}
            </span>
            {method.isDefault && (
              <span className="ml-2 text-caption text-status-success-text">
                (Default)
              </span>
            )}
          </p>
          <MembershipLine names={method.membershipNames} />
          <p className="text-caption text-foreground-secondary">
            <span>
              {method.brand
                ? method.expMonth && method.expYear
                  ? `Exp: ${method.expMonth}/${method.expYear}`
                  : null
                : "Bank account"}
            </span>
            {method.createdDate && (
              <span className="ml-1">
                • Added {formatDate(method.createdDate, true, false)}
              </span>
            )}
          </p>
        </div>
        <Tooltip content={bankAccountsTooltip} triggerAsChild={true}>
          <button
            type="button"
            className="shrink-0 rounded p-0.5 text-foreground-muted hover:bg-hover hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <FaInfoCircle className="h-3.5 w-3.5" />
          </button>
        </Tooltip>
      </div>
    );
  }

  const cardContent = (
    <>
      {(method.isDefault || actions) && (
        <div className="absolute right-1 top-1 z-10">
          {method.isDefault ? (
            <span className="inline-flex items-center rounded-full bg-status-success-bg px-2 py-0.5 text-caption-strong text-status-success-text">
              Default
            </span>
          ) : (
            actions
          )}
        </div>
      )}

      {method.brand ? (
        <>
          <div
            className={cx("mt-3 h-2.5 w-full", {
              "bg-foreground": isAvailable,
              "bg-foreground-muted": !isAvailable,
            })}
          />
          <div className="relative flex flex-col p-4">
            <div className="flex items-center space-x-3">
              <CardIcon paymentMethod={method} />
              <span
                className={cx("text-caption-strong", {
                  "text-muted-foreground": isAvailable,
                  "text-foreground-muted": !isAvailable,
                })}
              >
                •••• •••• •••• {method.last4}
              </span>
            </div>
            <div
              className={cx("mt-1 flex flex-col gap-0.5 text-caption", {
                "text-muted-foreground": isAvailable,
                "text-foreground-muted": !isAvailable,
              })}
            >
              <span>
                {method.expMonth && method.expYear
                  ? `Exp: ${method.expMonth}/${method.expYear}`
                  : null}
              </span>
              {method.createdDate && (
                <span>Added {formatDate(method.createdDate, true, false)}</span>
              )}
              <MembershipLine names={method.membershipNames} />
            </div>
            <div className="absolute bottom-1 right-1 z-10">
              <Tooltip content={bankAccountsTooltip} triggerAsChild={true}>
                <button
                  type="button"
                  className="rounded p-0.5 text-foreground-muted hover:bg-hover hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <FaInfoCircle className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="border-b border-dashed bg-background-secondary px-4 py-2">
            <div className="flex items-center space-x-2">
              <FaLandmark className="h-4 w-4 text-muted-foreground" />
              <span className="text-caption text-foreground-secondary">
                BANK ACCOUNT
              </span>
            </div>
          </div>
          <div className="relative p-3">
            <div className="text-body font-semibold">
              {method.bankName || "Bank Account"}
            </div>
            <div className="mt-1 flex justify-between text-caption">
              <span className="text-muted-foreground">Account:</span>
              <span className="text-foreground-secondary">
                •••• <span>{method.accountNumberLast4 || method.last4}</span>
              </span>
            </div>
            {method.createdDate && (
              <div className="mt-1 text-caption text-muted-foreground">
                Added {formatDate(method.createdDate, true, false)}
              </div>
            )}
            <div className="mt-1 flex">
              <MembershipLine names={method.membershipNames} />
            </div>
            <div className="absolute bottom-1 right-1 z-10">
              <Tooltip content={bankAccountsTooltip} triggerAsChild={true}>
                <button
                  type="button"
                  className="rounded p-0.5 text-foreground-muted hover:bg-hover hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaInfoCircle className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </div>
          </div>
        </>
      )}
    </>
  );

  const wrapperClassName = cx(
    "relative w-48 rounded-md border text-foreground transition",
    {
      "ring-2 ring-ring": isSelected && isAvailable,
      "cursor-not-allowed opacity-50": !isAvailable && isSelectable,
      "bg-background-secondary": !isAvailable && isSelectable,
      "cursor-pointer": isSelectable && isAvailable,
    },
    className,
  );

  if (isSelectable && onSelect) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={wrapperClassName}
        onClick={() => isAvailable && onSelect()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && isAvailable) {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        {cardContent}
      </div>
    );
  }

  return <div className={wrapperClassName}>{cardContent}</div>;
}
